import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import {
  StatusBadge,
  PageLoader,
  ErrorState,
  EmptyState,
  Field,
  Spinner,
  Modal,
  Pagination,
} from '../../components/ui';
import { newsApi, mediaApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';
import RichTextEditor from '../../components/RichTextEditor';
import GalleryEditor from '../../components/GalleryEditor';
import MoveNewsModal, { TYPE_LABEL } from '../../components/MoveNewsModal';
import { galleryItemsFromRecord, buildGalleryPayload, applyGalleryErrors } from '../../lib/gallery';

const EMPTY_FORM = {
  id: null,
  title: '',
  excerpt: '',
  body: '',
  featured_image_path: '',
  published_at: '', // YYYY-MM-DD (local) — blank means "not published"
  is_published: false,
  gallery: [],
};

/** Status filter tabs -> the API `status` param sent to newsApi.list. */
const STATUS_TABS = [
  { key: 'all', label: 'All', param: 'all' },
  { key: 'published', label: 'Published', param: undefined }, // admin default = published only
  { key: 'draft', label: 'Draft', param: 'draft' },
];

/** ISO datetime -> local YYYY-MM-DD for a <input type="date">. */
function isoToDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Best display URL for a news featured image (handles absolute + stored paths). */
function newsImage(item) {
  const path = item?.featured_image_path;
  if (path && /^https?:\/\//i.test(path)) return path;
  return item?.featured_image_url || assetUrl(path);
}

/**
 * Facebook sync marker (design spec §8.3): imported items link back to the
 * original Page post; pushed items link to the post the site created.
 */
function FacebookBadge({ facebook }) {
  if (!facebook) return null;
  const inbound = facebook.direction === 'inbound';
  if (!inbound && facebook.status !== 'posted') return null;
  const label = inbound ? 'From Facebook' : 'Posted to Facebook';
  const cls = 'pill bg-blue-100 text-blue-800';
  if (!facebook.permalink_url) return <span className={cls}>{label}</span>;
  return (
    <a
      href={facebook.permalink_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cls} hover:underline`}
      title="Open the Facebook post"
    >
      {label}
    </a>
  );
}

export default function AdminNews() {
  const toast = useToast();
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, error: null, items: [], meta: null });

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryDirty, setGalleryDirty] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [moving, setMoving] = useState(null);

  const load = useCallback(() => {
    const tab = STATUS_TABS.find((t) => t.key === status);
    setState((s) => ({ ...s, loading: true, error: null }));
    newsApi
      .list({ status: tab?.param, search: search || undefined, page, per_page: 10 })
      .then((res) =>
        setState({ loading: false, error: null, items: res.items, meta: res.meta })
      )
      .catch((error) => setState({ loading: false, error, items: [], meta: null }));
  }, [status, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setErrors({});
    setGalleryDirty(false);
  }

  /** Mark the gallery dirty and apply a functional update from GalleryEditor. */
  function setGallery(updater) {
    setForm((f) => ({ ...f, gallery: updater(f.gallery) }));
    setGalleryDirty(true);
  }

  async function editNews(item) {
    // Fetch the full record so the existing gallery loads even if a list row
    // ever omits it (mirrors AdminActivities editActivity).
    let full = item;
    try {
      full = await newsApi.get(item.slug || item.id);
    } catch {
      full = item;
    }
    setForm({
      id: full.id,
      title: full.title || '',
      excerpt: full.excerpt || '',
      body: full.body || '',
      featured_image_path: full.featured_image_path || '',
      published_at: isoToDateInput(full.published_at),
      is_published: !!full.is_published,
      gallery: galleryItemsFromRecord(full),
    });
    setErrors({});
    setGalleryDirty(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'image');
      fd.append('alt_text', file.name);
      const media = await mediaApi.upload(fd);
      const value = media?.url || media?.path || media?.file_path || '';
      if (!value) throw new Error('Upload succeeded but no URL was returned.');
      set('featured_image_path', value);
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed. You can paste an image path instead.');
    } finally {
      setUploading(false);
    }
  }

  /**
   * @param {'publish'|'draft'} mode
   *   publish -> published_at = chosen date, or now when left blank.
   *   draft   -> published_at = null (revert / keep as draft).
   */
  async function save(mode) {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (!form.body.trim()) {
      toast.error('Body is required.');
      return;
    }

    // Build the gallery payload up front so a missing media reference is caught
    // before we hit the API. Omitted entirely unless the gallery was touched.
    let galleryPayload;
    if (galleryDirty) {
      try {
        galleryPayload = buildGalleryPayload(form.gallery); // may be [] to clear
      } catch (err) {
        toast.error(err.message);
        return;
      }
    }

    setErrors({});
    setBusy(true);
    try {
      const published_at =
        mode === 'publish'
          ? form.published_at || new Date().toISOString()
          : null;
      const payload = {
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || null,
        body: form.body,
        featured_image_path: form.featured_image_path || null,
        published_at,
      };
      if (galleryDirty) payload.gallery = galleryPayload;
      const saved = form.id
        ? await newsApi.update(form.id, payload)
        : await newsApi.create(payload);
      toast.success(
        mode === 'publish'
          ? saved?.is_published
            ? 'News published.'
            : 'News saved.'
          : form.id
            ? 'Reverted to draft.'
            : 'Draft saved.'
      );
      resetForm();
      load();
    } catch (err) {
      let handled = false;
      if (err.status === 422 && galleryDirty) {
        const { items, matched } = applyGalleryErrors(form.gallery, err.errors);
        if (matched) {
          setForm((f) => ({ ...f, gallery: items }));
          toast.error('Please fix the highlighted gallery items.');
          handled = true;
        }
      }
      if (!handled) {
        setErrors(err.errors || {});
        toast.error(err.message || 'Could not save news.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(item) {
    setBusy(true);
    try {
      await newsApi.remove(item.id);
      toast.success('News deleted.');
      setConfirmDelete(null);
      if (form.id === item.id) resetForm();
      load();
    } catch (err) {
      toast.error(err.message || 'Could not delete news.');
    } finally {
      setBusy(false);
    }
  }

  /** Where the moved item now lives in the admin, by type. */
  function movedAdminPath(result) {
    switch (result.type) {
      case 'activity':
        return '/admin/activities';
      case 'story':
        return '/admin/stories';
      case 'event':
        return '/admin/events';
      case 'event_recap':
        return result.event?.slug ? `/admin/events/${result.event.slug}/recaps` : '/admin/events';
      default:
        return '/admin';
    }
  }

  function handleMoved(result) {
    const title = result.item?.title || moving?.title || '';
    setMoving(null);
    toast.success(
      <span>
        Moved to {TYPE_LABEL[result.type] || result.type}: {title}{' '}
        <Link to={movedAdminPath(result)} className="underline">
          Open
        </Link>
      </span>
    );
    load();
  }

  function applySearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(query.trim());
  }

  return (
    <div className="space-y-6">
      <Seo title="Manage News" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">News &amp; Highlights</h1>
        {form.id && (
          <button onClick={resetForm} className="btn-ghost text-sm" disabled={busy}>
            + New post
          </button>
        )}
      </div>

      {/* Create / edit panel */}
      <div className="card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">
            {form.id ? 'Edit news post' : 'New news post'}
          </h2>
          {form.id && (
            <StatusBadge status={form.is_published ? 'published' : 'draft'} />
          )}
        </div>

        <div className="grid gap-4">
          <Field label="Title *" error={errors.title?.[0]}>
            <input
              className="input"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Headline"
            />
          </Field>

          <Field
            label="Excerpt"
            hint="A short summary shown on cards and in search results."
            error={errors.excerpt?.[0]}
          >
            <textarea
              className="input"
              rows={2}
              value={form.excerpt}
              onChange={(e) => set('excerpt', e.target.value)}
            />
          </Field>

          <Field
            label="Body *"
            hint="Format text, add links, insert images, and build tables."
            error={errors.body?.[0]}
          >
            <RichTextEditor
              id="news-body"
              value={form.body}
              onChange={(html) => set('body', html)}
              placeholder="Write the article…"
            />
          </Field>

          <Field
            label="Featured image"
            error={errors.featured_image_path?.[0]}
            hint="Upload an image or paste an existing path."
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <input type="file" accept="image/*" onChange={handleImage} className="text-sm" />
              <input
                className="input"
                placeholder="news/my-image.jpg"
                value={form.featured_image_path}
                onChange={(e) => set('featured_image_path', e.target.value)}
              />
            </div>
            {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
            {form.featured_image_path && (
              <img
                src={newsImage(form)}
                alt="Featured preview"
                className="mt-3 aspect-video w-full max-w-sm rounded-lg object-cover shadow-card"
              />
            )}
          </Field>

          <Field
            label="Publish date"
            hint="Leave blank to publish with today's date. “Save as Draft” clears this and hides the post from the public."
            error={errors.published_at?.[0]}
          >
            <input
              type="date"
              className="input max-w-xs"
              value={form.published_at}
              onChange={(e) => set('published_at', e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-6">
          <GalleryEditor
            items={form.gallery}
            onChange={setGallery}
            disabled={busy}
            onUploadingChange={setGalleryUploading}
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-cream-300 pt-5">
          <button
            onClick={() => save('publish')}
            className="btn-primary"
            disabled={busy || uploading || galleryUploading}
          >
            {busy ? <Spinner className="h-4 w-4" /> : form.is_published ? 'Save & Keep Published' : 'Save & Publish'}
          </button>
          <button
            onClick={() => save('draft')}
            className="btn-outline"
            disabled={busy || uploading || galleryUploading}
          >
            {form.id && form.is_published ? 'Revert to Draft' : 'Save as Draft'}
          </button>
          {form.id && (
            <button onClick={resetForm} className="btn-ghost ml-auto" disabled={busy}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Toolbar: search + status filter */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={applySearch} className="flex w-full max-w-md gap-2">
          <input
            className="input"
            placeholder="Search news…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search news"
          />
          <button type="submit" className="btn-primary px-4">
            Search
          </button>
        </form>

        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setPage(1);
                setStatus(t.key);
              }}
              className={`pill cursor-pointer transition-colors ${
                status === t.key
                  ? 'bg-maroon-700 text-white'
                  : 'bg-surface-gray text-ink-soft hover:bg-cream-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {state.loading ? (
        <PageLoader />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : state.items.length === 0 ? (
        <EmptyState
          title={search ? 'No matching news' : 'No news yet'}
          message={
            search
              ? `No results for “${search}”.`
              : 'Create your first post using the form above.'
          }
        />
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
                <tr>
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Published</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-300">
                {state.items.map((n) => (
                  <tr key={n.id} className="hover:bg-surface-alt">
                    <td className="px-5 py-3 font-medium text-ink">
                      <button
                        onClick={() => editNews(n)}
                        className="text-left hover:text-maroon-700"
                      >
                        {n.title}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={n.is_published ? 'published' : 'draft'} />
                        <FacebookBadge facebook={n.facebook} />
                      </div>
                    </td>
                    <td className="px-5 py-3 text-ink-muted">
                      {n.published_at ? formatDate(n.published_at) : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <a
                          href={`/news/${n.slug || n.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-ghost px-3 py-1.5 text-xs"
                        >
                          View
                        </a>
                        <button
                          onClick={() => editNews(n)}
                          className="btn-outline px-3 py-1.5 text-xs"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setMoving(n)}
                          className="btn-outline px-3 py-1.5 text-xs"
                          disabled={busy}
                        >
                          Move to…
                        </button>
                        <button
                          onClick={() => setConfirmDelete(n)}
                          className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                          disabled={busy}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination meta={state.meta} onChange={(p) => setPage(p)} />
        </>
      )}

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete news post?"
        footer={
          <>
            <button onClick={() => setConfirmDelete(null)} className="btn-ghost" disabled={busy}>
              Cancel
            </button>
            <button
              onClick={() => handleDelete(confirmDelete)}
              className="btn-danger"
              disabled={busy}
            >
              {busy ? 'Deleting…' : 'Delete'}
            </button>
          </>
        }
      >
        {confirmDelete && (
          <p className="text-sm text-ink-muted">
            “{confirmDelete.title}” will be permanently removed. This cannot be undone.
          </p>
        )}
      </Modal>

      <MoveNewsModal news={moving} onClose={() => setMoving(null)} onMoved={handleMoved} />
    </div>
  );
}
